import { THEMES, type ThemeKey, type ThemeParticles } from '@veriverse/shared';

/** How a seasonal theme looks on screen (spec §6.12). Pure, so it can be tested. */
export interface ThemeVisuals {
  /** Colour laid lightly over the whole view (0 alpha = none). */
  readonly tint: number;
  readonly tintAlpha: number;
  readonly particles: ThemeParticles;
  /** Particles on screen at once. */
  readonly count: number;
}

export const NO_THEME: ThemeVisuals = { tint: 0xffffff, tintAlpha: 0, particles: 'none', count: 0 };

/** Theme particles are decoration: few of them, and none with reduced motion. */
const PARTICLE_COUNT: Record<ThemeParticles, number> = {
  none: 0,
  snow: 60,
  leaves: 24,
  petals: 30,
  sparkles: 40,
};

export function themeVisuals(key: ThemeKey | null, reducedMotion: boolean): ThemeVisuals {
  if (!key) return NO_THEME;
  const theme = THEMES[key];
  return {
    tint: theme.tint,
    tintAlpha: theme.tintAlpha,
    particles: reducedMotion ? 'none' : theme.particles,
    count: reducedMotion ? 0 : PARTICLE_COUNT[theme.particles],
  };
}

/** Particle colours per preset; one is picked per particle. */
export const PARTICLE_COLORS: Record<ThemeParticles, readonly number[]> = {
  none: [],
  snow: [0xffffff, 0xe8f1ff],
  leaves: [0xd2691e, 0xe8a33d, 0x8b3a1a],
  petals: [0xffffff, 0xffd1e8, 0xfff3a0],
  sparkles: [0xfff2b0, 0xffd27a],
};

/** Fall speed (px/s) and sideways sway (px) per preset; sparkles stay put and twinkle. */
export const PARTICLE_MOTION: Record<ThemeParticles, { fall: number; sway: number }> = {
  none: { fall: 0, sway: 0 },
  snow: { fall: 22, sway: 6 },
  leaves: { fall: 30, sway: 14 },
  petals: { fall: 18, sway: 10 },
  sparkles: { fall: 0, sway: 0 },
};

/** Which theme to show: a local preview wins, then the server's `themeId`. */
export function shownTheme(
  serverThemeId: string | undefined,
  preview: ThemeKey | 'off' | undefined,
): ThemeKey | null {
  if (preview === 'off') return null;
  if (preview) return preview;
  return serverThemeId && serverThemeId in THEMES ? (serverThemeId as ThemeKey) : null;
}
